import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-hot-toast";
import * as THREE from "three";
import {
  ArrowRight,
  BarChart3,
  CalendarCheck,
  CheckCircle2,
  CircleCheck,
  Clock3,
  Eye,
  EyeOff,
  Layers3,
  LockKeyhole,
  Mail,
  MessageSquareText,
  Phone,
  ShieldCheck,
  UsersRound,
  X,
} from "lucide-react";
import { API_BASE_URL } from "../../app/apiConfig";
import { createQuoteRequestApi } from "../../app/api";
import { connectSocket } from "../../socket/socket";
import { markAuthSessionStarted } from "../../utils/authSession";

const features = [
  {
    title: "Attendance intelligence",
    description: "Punch-in, breaks, screenshots, late alerts, monthly reports, and admin visibility in one controlled flow.",
    icon: Clock3,
  },
  {
    title: "Tasks and productivity",
    description: "Assign work, track progress, review timelines, measure accuracy, and keep teams accountable.",
    icon: CalendarCheck,
  },
  {
    title: "Team chat and calls",
    description: "Secure messaging, groups, attachments, notifications, and calling built directly into the workspace.",
    icon: MessageSquareText,
  },
  {
    title: "HR operations suite",
    description: "Leaves, employee profiles, documents, payroll, inventory, expenses, and approvals for growing teams.",
    icon: UsersRound,
  },
];

const metrics = [
  { value: "360°", label: "Employee operations" },
  { value: "Real-time", label: "Admin visibility" },
  { value: "Web + App", label: "iOS and Android ready" },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    contact_name: "",
    email: "",
    phone: "",
    query: "",
  });

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE_URL}/users/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        Cookies.set("token", data.data.token, {
          expires: 7,
          sameSite: "lax",
        });

        localStorage.setItem("user", JSON.stringify(data.data.user));
        markAuthSessionStarted();
        toast.success("Login successful");
        connectSocket();

        setTimeout(() => {
          window.location.href = "/";
        }, 500);
      } else {
        toast.error(data.message || "Login failed");
      }
    } catch {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteSubmit = async (event) => {
    event.preventDefault();
    setQuoteLoading(true);

    try {
      const res = await createQuoteRequestApi(quoteForm);
      if (res?.success) {
        toast.success("Quote request submitted");
        setQuoteOpen(false);
        setQuoteForm({ contact_name: "", email: "", phone: "", query: "" });
      } else {
        toast.error(res?.message || "Failed to submit quote request");
      }
    } finally {
      setQuoteLoading(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#07100f] text-white">
      <ThreeShowcase />

      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(7,16,15,0.96)_0%,rgba(7,16,15,0.88)_38%,rgba(7,16,15,0.56)_100%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,16,15,0.2)_0%,rgba(7,16,15,0.08)_48%,rgba(7,16,15,0.94)_100%)]" />

      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="mx-auto flex w-full max-w-7xl items-center justify-between px-5 py-5 md:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-emerald-300/30 bg-emerald-300/10">
              <Layers3 size={21} className="text-emerald-200" />
            </div>
            <div>
              <p className="text-sm font-semibold">UPTASK</p>
              <p className="text-xs text-white/50">by Visiontrek Communications</p>
            </div>
          </div>

          <nav className="hidden items-center gap-7 text-sm text-white/65 md:flex">
            <a href="#features" className="transition hover:text-white">Features</a>
            <button type="button" onClick={() => setQuoteOpen(true)} className="transition hover:text-white">Request quote</button>
            <a href="tel:+918950022334" className="transition hover:text-white">+91-8950022334</a>
          </nav>

          <button
            type="button"
            onClick={() => setLoginOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-[#07100f] shadow-[0_14px_40px_rgba(255,255,255,0.18)] transition hover:bg-emerald-100"
          >
            Login
            <LockKeyhole size={16} />
          </button>
        </header>

        <section className="mx-auto grid w-full max-w-7xl flex-1 items-center gap-10 px-5 pb-14 pt-10 md:grid-cols-[1.08fr_0.92fr] md:px-8 md:pb-20 md:pt-16">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-lg border border-emerald-300/25 bg-emerald-300/10 px-3 py-2 text-xs font-semibold text-emerald-100">
              <CheckCircle2 size={15} />
              Complete HR, attendance, task and team operations platform
            </div>

            <h1 className="max-w-4xl text-5xl font-semibold leading-tight text-white md:text-7xl">
              Run your company from one intelligent workspace.
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-white/68 md:text-lg">
              UPTASK helps organizations manage employee attendance, tasks, chats, projects, documents,
              leaves, payroll, inventory, expenses, and approvals from a single professional system.
            </p>

            <div id="contact" className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-300 px-5 py-3 text-sm font-bold text-[#07100f] shadow-[0_18px_55px_rgba(110,231,183,0.22)] transition hover:bg-emerald-200"
              >
                Request quote
                <MessageSquareText size={17} />
              </button>
              <a
                href="tel:+918950022334"
                className="inline-flex items-center gap-2 rounded-lg border border-emerald-200/30 bg-emerald-200/10 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-200/15"
              >
                Call Visiontrek
                <Phone size={17} />
              </a>
              <button
                type="button"
                onClick={() => setLoginOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/8 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/12"
              >
                Existing customer login
                <ArrowRight size={17} />
              </button>
            </div>

            <div className="mt-10 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
              {metrics.map((metric) => (
                <div key={metric.label} className="rounded-lg border border-white/10 bg-white/[0.055] p-4 backdrop-blur">
                  <p className="text-xl font-bold text-white">{metric.value}</p>
                  <p className="mt-1 text-xs leading-5 text-white/55">{metric.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:block">
            <SystemPreview />
          </div>
        </section>

        <section id="features" className="border-t border-white/10 bg-[#07100f]/85 px-5 py-12 backdrop-blur md:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <p className="text-sm font-semibold text-emerald-200">Main features</p>
                <h2 className="mt-2 text-3xl font-semibold">Everything teams need to stay visible and accountable.</h2>
              </div>
              <p className="max-w-xl text-sm leading-6 text-white/55">
                Built for businesses that need practical control, clean workflows, and fast employee communication.
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-4">
              {features.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article key={feature.title} className="rounded-lg border border-white/10 bg-white/[0.045] p-5 transition hover:border-emerald-300/35 hover:bg-white/[0.07]">
                    <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-lg bg-white/8 text-emerald-200">
                      <Icon size={21} />
                    </div>
                    <h3 className="text-base font-semibold">{feature.title}</h3>
                    <p className="mt-3 text-sm leading-6 text-white/55">{feature.description}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <footer className="border-t border-white/10 bg-[#050b0a] px-5 py-5 text-center text-xs text-white/45 md:px-8">
          @copyright {new Date().getFullYear()} Visiontrek Communications. For query contact +91-8950022334.
        </footer>
      </div>

      {loginOpen && (
        <LoginDialog
          email={email}
          password={password}
          showPassword={showPassword}
          loading={loading}
          setEmail={setEmail}
          setPassword={setPassword}
          setShowPassword={setShowPassword}
          onClose={() => setLoginOpen(false)}
          onSubmit={handleLogin}
        />
      )}

      {quoteOpen && (
        <QuoteDialog
          form={quoteForm}
          loading={quoteLoading}
          setForm={setQuoteForm}
          onClose={() => setQuoteOpen(false)}
          onSubmit={handleQuoteSubmit}
        />
      )}
    </main>
  );
}

function LoginDialog({
  email,
  password,
  showPassword,
  loading,
  setEmail,
  setPassword,
  setShowPassword,
  onClose,
  onSubmit,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/72 px-4 backdrop-blur-xl">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(110,231,183,0.18),transparent_34%),radial-gradient(circle_at_70%_70%,rgba(103,232,249,0.12),transparent_30%)]" />

      <div className="relative w-full max-w-[760px] overflow-hidden rounded-xl border border-white/15 bg-[#08110f]/92 text-white shadow-[0_35px_120px_rgba(0,0,0,0.72)]">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-200/70 to-transparent" />
        <div className="grid md:grid-cols-[0.92fr_1.08fr]">
          <aside className="relative hidden min-h-[440px] border-r border-white/10 bg-white/[0.045] p-7 md:block">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(110,231,183,0.22),transparent_36%),radial-gradient(circle_at_80%_75%,rgba(251,191,36,0.12),transparent_34%)]" />
            <div className="relative flex h-full flex-col justify-between">
              <div>
                <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-emerald-200/30 bg-emerald-300/12 shadow-[0_0_35px_rgba(110,231,183,0.18)]">
                  <Layers3 size={24} className="text-emerald-100" />
                </div>
                <p className="mt-7 text-xs font-bold uppercase tracking-[0.32em] text-emerald-100/80">
                  Visiontrek
                </p>
                <h3 className="mt-3 text-3xl font-semibold leading-tight">
                  One workspace for serious teams.
                </h3>
                <p className="mt-4 text-sm leading-7 text-white/58">
                  Attendance, tasks, HR approvals, chat, reports and operations in a secure business system.
                </p>
              </div>

              <div className="space-y-3">
                {["Role based access", "Realtime alerts", "Web, iOS and Android ready"].map((item) => (
                  <div key={item} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/18 px-3 py-2 text-sm text-white/72">
                    <CircleCheck size={15} className="text-emerald-200" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <div className="p-6 sm:p-8">
            <div className="mb-7 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/25 bg-emerald-300/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-100">
                  <ShieldCheck size={14} />
                  Secure access
                </div>
                <h2 className="mt-4 text-3xl font-semibold tracking-tight">Welcome back</h2>
                <p className="mt-2 text-sm text-white/55">Login to UPTASK and continue managing operations.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/55 transition hover:bg-white/10 hover:text-white"
                aria-label="Close login"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
                  Email or username
                </span>
                <div className="relative">
                  <Mail size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-emerald-100/55" />
                  <input
                    type="text"
                    placeholder="name@company.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className="w-full rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 pl-12 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
                    required
                  />
                </div>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
                  Password
                </span>
                <div className="relative">
                  <LockKeyhole size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-emerald-100/55" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="w-full rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 pl-12 pr-12 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
                    required
                  />

                  <button
                    type="button"
                    onClick={() => password && setShowPassword((value) => !value)}
                    disabled={!password}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/50 transition hover:bg-white/10 hover:text-white disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="group mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 px-4 py-3.5 text-sm font-black text-[#07100f] shadow-[0_18px_50px_rgba(110,231,183,0.24)] transition hover:scale-[1.01] hover:shadow-[0_24px_70px_rgba(110,231,183,0.3)] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-65"
              >
                {loading ? "Signing in..." : "Enter workspace"}
                {!loading && <ArrowRight size={17} className="transition group-hover:translate-x-0.5" />}
              </button>

              <div className="flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.035] px-3 py-3 text-xs text-white/45">
                <span>Protected Visiontrek workspace</span>
                <span className="font-semibold text-emerald-100/80">Encrypted</span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuoteDialog({ form, loading, setForm, onClose, onSubmit }) {
  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/72 px-4 backdrop-blur-xl">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_45%_30%,rgba(110,231,183,0.2),transparent_32%),radial-gradient(circle_at_72%_78%,rgba(251,191,36,0.12),transparent_28%)]" />

      <div className="relative w-full max-w-2xl overflow-hidden rounded-xl border border-white/15 bg-[#08110f]/94 p-6 text-white shadow-[0_35px_120px_rgba(0,0,0,0.72)] sm:p-8">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-200/70 to-transparent" />

        <div className="mb-7 flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/25 bg-emerald-300/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-100">
              <MessageSquareText size={14} />
              Request quote
            </div>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight">Tell us what you need</h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-white/55">
              Share your contact details and requirement. Visiontrek admin will review it inside UPTASK.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-white/10 bg-white/[0.04] p-2 text-white/55 transition hover:bg-white/10 hover:text-white"
            aria-label="Close quote request"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
                Contact person
              </span>
              <input
                value={form.contact_name}
                onChange={(event) => updateField("contact_name", event.target.value)}
                placeholder="Your name"
                className="w-full rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
                required
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
                Email
              </span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                placeholder="name@company.com"
                className="w-full rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
                required
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
              Contact number
            </span>
            <input
              value={form.phone}
              onChange={(event) => updateField("phone", event.target.value)}
              placeholder="+91..."
              className="w-full rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-white/42">
              Query
            </span>
            <textarea
              value={form.query}
              onChange={(event) => updateField("query", event.target.value)}
              placeholder="Tell us about team size, modules needed, web/app requirement, timeline, or any customization."
              rows={5}
              className="w-full resize-none rounded-lg border border-white/12 bg-white/[0.075] px-4 py-3.5 text-sm leading-6 text-white outline-none transition placeholder:text-white/30 focus:border-emerald-200/80 focus:bg-white/[0.105] focus:ring-4 focus:ring-emerald-300/10"
              required
            />
          </label>

          <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
            <p className="rounded-lg border border-white/10 bg-white/[0.035] px-3 py-3 text-xs leading-5 text-white/45">
              You can also call Visiontrek directly at <span className="font-semibold text-emerald-100">+91-8950022334</span>.
            </p>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-emerald-300 via-teal-200 to-cyan-200 px-5 py-3.5 text-sm font-black text-[#07100f] shadow-[0_18px_50px_rgba(110,231,183,0.24)] transition hover:scale-[1.01] disabled:cursor-not-allowed disabled:opacity-65"
            >
              {loading ? "Sending..." : "Send request"}
              {!loading && <ArrowRight size={17} />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SystemPreview() {
  return (
    <div className="ml-auto max-w-lg rounded-lg border border-white/10 bg-white/[0.06] p-4 shadow-[0_30px_100px_rgba(0,0,0,0.35)] backdrop-blur">
      <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
        <div>
          <p className="text-sm font-semibold">Live operations console</p>
          <p className="text-xs text-white/45">Admin overview</p>
        </div>
        <span className="rounded-lg bg-emerald-300/15 px-3 py-1 text-xs font-semibold text-emerald-100">Online</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <PreviewTile icon={UsersRound} label="Employees" value="128" tone="emerald" />
        <PreviewTile icon={Clock3} label="Present today" value="96%" tone="cyan" />
        <PreviewTile icon={BarChart3} label="Tasks closed" value="342" tone="amber" />
        <PreviewTile icon={ShieldCheck} label="Approvals" value="18" tone="rose" />
      </div>

      <div className="mt-4 rounded-lg border border-white/10 bg-[#061110]/70 p-4">
        <div className="mb-3 flex items-center justify-between text-xs text-white/45">
          <span>Workflow health</span>
          <span>94%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full w-[94%] rounded-full bg-emerald-300" />
        </div>
        <div className="mt-4 space-y-3">
          {["Payroll review ready", "4 leave requests pending", "New project submitted"].map((item) => (
            <div key={item} className="flex items-center gap-3 text-sm text-white/70">
              <CheckCircle2 size={15} className="text-emerald-200" />
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewTile({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "text-emerald-200 bg-emerald-300/10",
    cyan: "text-cyan-200 bg-cyan-300/10",
    amber: "text-amber-200 bg-amber-300/10",
    rose: "text-rose-200 bg-rose-300/10",
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.045] p-4">
      <div className={`mb-4 flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}>
        <Icon size={18} />
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="mt-1 text-xs text-white/48">{label}</p>
    </div>
  );
}

function ThreeShowcase() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.set(0, 0, 9);

    const group = new THREE.Group();
    group.position.set(2.1, 0.05, 0);
    scene.add(group);

    const core = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.45, 2),
      new THREE.MeshStandardMaterial({
        color: 0x6ee7b7,
        roughness: 0.28,
        metalness: 0.72,
        transparent: true,
        opacity: 0.9,
      })
    );
    group.add(core);

    const ringMaterial = new THREE.MeshBasicMaterial({ color: 0x67e8f9, transparent: true, opacity: 0.52 });
    const ringOne = new THREE.Mesh(new THREE.TorusGeometry(2.1, 0.012, 16, 120), ringMaterial);
    const ringTwo = new THREE.Mesh(new THREE.TorusGeometry(2.7, 0.01, 16, 120), ringMaterial.clone());
    ringOne.rotation.x = Math.PI / 2.7;
    ringTwo.rotation.y = Math.PI / 2.4;
    group.add(ringOne, ringTwo);

    const nodeMaterial = new THREE.MeshStandardMaterial({ color: 0xfbbf24, emissive: 0x3a2400, roughness: 0.35 });
    const nodeGeometry = new THREE.SphereGeometry(0.075, 16, 16);
    const nodes = [];
    for (let i = 0; i < 26; i += 1) {
      const node = new THREE.Mesh(nodeGeometry, nodeMaterial);
      const angle = (i / 26) * Math.PI * 2;
      const radius = 2.15 + (i % 3) * 0.24;
      node.position.set(Math.cos(angle) * radius, Math.sin(angle * 1.7) * 0.55, Math.sin(angle) * radius * 0.38);
      nodes.push(node);
      group.add(node);
    }

    const particleCount = 700;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i += 1) {
      positions[i * 3] = (Math.random() - 0.5) * 13;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 8;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 7;
    }

    const particleGeometry = new THREE.BufferGeometry();
    particleGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const particles = new THREE.Points(
      particleGeometry,
      new THREE.PointsMaterial({ color: 0xffffff, size: 0.018, transparent: true, opacity: 0.38 })
    );
    scene.add(particles);

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const keyLight = new THREE.PointLight(0x6ee7b7, 42, 18);
    keyLight.position.set(3, 3, 4);
    scene.add(keyLight);
    const rimLight = new THREE.PointLight(0xf59e0b, 18, 16);
    rimLight.position.set(-4, -2, 3);
    scene.add(rimLight);

    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      group.rotation.y += 0.004;
      group.rotation.x = Math.sin(Date.now() * 0.00045) * 0.12;
      ringOne.rotation.z += 0.007;
      ringTwo.rotation.x -= 0.004;
      particles.rotation.y += 0.0007;
      renderer.render(scene, camera);
    };

    const resize = () => {
      const width = mount.clientWidth;
      const height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };

    window.addEventListener("resize", resize);
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
      mount.removeChild(renderer.domElement);
      particleGeometry.dispose();
      core.geometry.dispose();
      core.material.dispose();
      ringOne.geometry.dispose();
      ringOne.material.dispose();
      ringTwo.geometry.dispose();
      ringTwo.material.dispose();
      nodeGeometry.dispose();
      nodeMaterial.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} className="absolute inset-0" aria-hidden="true" />;
}
